import Box from '@mui/material/Box';
import { ReactNode, useRef, useState } from 'react';
import { SearchProvider, SearchProviderProps } from './search/SearchProvider';
import { ChatLayer } from '../../salesforce-documentation-card/ChatLayer';
import OpenAiSection from '../documentation-card/open-ai/OpenAiSection';
import { SalesforceItem } from '../../../parser/ConfigurationItemTypes';
import { OpenAiDescription } from '../../../parser/ParserTypes';

type SearchProviderConfig = Omit<SearchProviderProps, 'children' | 'scrollContainerRef'>;

interface ChatConfig {
  type: string;
  name: string;
  configurationItem: SalesforceItem;
  crmOrgId: string;
  itemType: string;
  configurationChatObjectName: string;
}

interface HeaderHandlers {
  onOpenChat: () => void;
}

interface DocumentationCardContentLayoutProps {
  searchProviderProps: SearchProviderConfig;
  renderHeader: (handlers: HeaderHandlers) => ReactNode;
  openAiDescription?: OpenAiDescription;
  showAiSection?: boolean;
  chatConfig?: ChatConfig;
  children: ReactNode;
}

export const DocumentationCardContentLayout = ({
  searchProviderProps,
  renderHeader,
  openAiDescription,
  showAiSection = false,
  chatConfig,
  children,
}: DocumentationCardContentLayoutProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [displayChat, setDisplayChat] = useState(false);

  return (
    <SearchProvider {...searchProviderProps} scrollContainerRef={scrollContainerRef}>
      {renderHeader({ onOpenChat: () => setDisplayChat(true) })}

      <Box position="relative" height="100%" overflow="hidden" width="100%">
        <Box ref={scrollContainerRef} p={2} overflow="scroll" height="100%">
          {showAiSection && (
            <Box pb={2}>
              <OpenAiSection openAiDescription={openAiDescription} />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, width: '100%' }}>
            {children}
          </Box>
        </Box>
      </Box>

      {displayChat && chatConfig && (
        <Box
          sx={{
            position: 'absolute',
            top: '28px',
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <ChatLayer
            type={chatConfig.type}
            name={chatConfig.name}
            configurationItem={chatConfig.configurationItem}
            crmOrgId={chatConfig.crmOrgId}
            itemType={chatConfig.itemType}
            configurationChatObjectName={chatConfig.configurationChatObjectName}
            onClose={() => setDisplayChat(false)}
          />
        </Box>
      )}
    </SearchProvider>
  );
};
